import React, { useCallback, useState } from "react";
import MyTasks from "./MyTasks";
import UnclaimedTasks from "./UnclaimedTasks";
import PageHeader from "components/PageHeader";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Global404 from "components/Errors/Global404";
import DashboardLayout from "components/Layouts/DashboardLayout";
import {
	Button,
	IconButton,
	SearchInput,
	Select,
} from "@alm/flow-ds/components";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { useDispatch } from "react-redux";
import { setCheckedBoxes } from "data/app/appActions";

function Tasks() {
	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();
	const reduxDispatch = useDispatch();

	const [searchBox, setSearchBox] = useState("");
	const [searchValue, setSearchValue] = useState("");
	const [mobileOpen, setMobileOpen] = useState(false);

	/**
	 * Handles a search change
	 */
	const handleSearchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Search input valued changed to "${value}"`,
			});

			if (value !== "") {
				setSearchBox(value);
			} else {
				setSearchBox(value);
				setSearchValue(value);
			}
		},
		[]
	);

	/**
	 * Handles a change in select box
	 *
	 * @param e select event
	 */
	const handleSelectChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.currentTarget.value;
			addSentryBreadcrumb({
				category: "input.change",
				message: `Select value changed to "${value}"`,
			});

			history.push(value);
			if (searchBox !== "") setSearchBox("");
			// Clear checkboxes
			reduxDispatch(setCheckedBoxes([]));
		},
		[history, reduxDispatch, searchBox]
	);

	/**
	 * Handles clicking the start activity button
	 */
	const handleStartActivityClick = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Start activity",
		});

		history.push("/activities/start");
	}, [history]);

	return (
		<DashboardLayout>
			<div className="container tasks">
				<div className="list-group-fixed-header">
					<PageHeader
						title={t("main.tasks.headings.pageTitle")}
						className="page-header__tasks">
						<div>
							<div
								className="search-bar search-bar--icon"
								aria-expanded={mobileOpen}>
								<IconButton
									aria-label={t("main.icons.openSearchBox")}
									active={mobileOpen}
									className="btn-basic--primary btn--round"
									icon="search"
									onClick={() => setMobileOpen(!mobileOpen)}
								/>
							</div>
							<Button
								className="btn-primary tasks__start-btn text-nowrap"
								label={t("main.activities.startActivity")}
								onClick={handleStartActivityClick}
							/>
						</div>
					</PageHeader>
					<div className="nav tasks__nav">
						{mobileOpen && (
							<div className="search-bar--mobile">
								<SearchInput
									label={t("main.text.search")}
									onSearch={() => setSearchValue(searchBox)}
									onChange={handleSearchChange}
									value={searchBox}
								/>
							</div>
						)}
						<Select
							className="tasks__select"
							value={location.pathname}
							onChange={handleSelectChange}
							label={t("main.tasks.labels.typeOfTasks")}
							labelHidden>
							<>
								<option value="/tasks/my-tasks">
									{t("main.tasks.headings.yourTasks")}
								</option>
								<option value="/tasks/open-tasks">
									{t("main.tasks.headings.unassignedTasks")}
								</option>
							</>
						</Select>
						<div className="search-bar search-bar--desktop">
							<SearchInput
								label={t("main.text.search")}
								onSearch={() => setSearchValue(searchBox)}
								onChange={handleSearchChange}
								value={searchBox}
							/>
						</div>
					</div>
				</div>

				<Switch>
					<Route
						exact
						path="/tasks/my-tasks"
						render={() => <MyTasks search={searchValue} />}
					/>
					<Route
						exact
						path="/tasks/open-tasks"
						render={() => <UnclaimedTasks search={searchValue} />}
					/>
					<Route component={Global404} />
				</Switch>
			</div>
		</DashboardLayout>
	);
}

export default Tasks;
